/**
 * MaterialThumbnail — aperçu visuel d'un matériau.
 * Si pas d'image (`src` null), génère un fallback procédural (gradient + pattern)
 * basé sur la couleur dominante détectée par l'IA.
 */
const PATTERNS = {
  wood: (c) => `repeating-linear-gradient(90deg, ${c} 0, ${c} 6px, ${shade(c, -10)} 6px, ${shade(c, -10)} 7px), linear-gradient(135deg, ${shade(c, 6)} 0%, ${c} 60%, ${shade(c, -12)} 100%)`,
  stone: (c) => `radial-gradient(circle at 25% 30%, ${shade(c, 12)} 0%, transparent 35%), radial-gradient(circle at 75% 70%, ${shade(c, -10)} 0%, transparent 40%), linear-gradient(135deg, ${c} 0%, ${shade(c, -6)} 100%)`,
  tile: (c) => `repeating-linear-gradient(0deg, ${shade(c, -18)} 0, ${shade(c, -18)} 1px, ${c} 1px, ${c} 24px), repeating-linear-gradient(90deg, ${shade(c, -18)} 0, ${shade(c, -18)} 1px, transparent 1px, transparent 24px)`,
  fabric: (c) => `repeating-linear-gradient(45deg, ${c} 0, ${c} 3px, ${shade(c, -8)} 3px, ${shade(c, -8)} 6px), repeating-linear-gradient(-45deg, transparent 0, transparent 3px, ${shade(c, -4)} 3px, ${shade(c, -4)} 4px)`,
  flat: (c) => `linear-gradient(135deg, ${shade(c, 4)} 0%, ${c} 50%, ${shade(c, -8)} 100%)`,
};

// Normalize a color string into a valid `#RRGGBB`. Accepts:
//   - "#RRGGBB"  → kept as-is
//   - "RRGGBB"   → prefixed with "#"
//   - rgb()/rgba() / named CSS colors → returned as-is (shade() simply skips them)
//   - null / undefined / "" → safe fallback (#5A5A5A)
function normalizeHex(c) {
  if (!c || typeof c !== 'string') return '#5A5A5A';
  const trimmed = c.trim();
  if (trimmed.startsWith('#')) return trimmed;
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
  if (/^[0-9A-Fa-f]{3}$/.test(trimmed)) return `#${trimmed}`;
  return trimmed;  // rgb()/named — shade() will safely no-op
}

function shade(hex, percent) {
  const h = normalizeHex(hex);
  if (!h || !h.startsWith('#') || h.length < 7) return h;
  const num = parseInt(h.slice(1), 16);
  if (Number.isNaN(num)) return h;
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * percent / 100)));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * percent / 100)));
  const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round(255 * percent / 100)));
  return `rgb(${r}, ${g}, ${b})`;
}

const MaterialThumbnail = ({
  src,
  color = '#5A5A5A',
  pattern = 'flat',
  size = 64,
  rounded = 12,
  label,
  className = '',
}) => {
  // Normalize once so the same color string works whether it comes from the LLM
  // (often without `#`) or from a hand-crafted seed (always with `#`).
  const normalizedColor = normalizeHex(color);
  // Some LLM outputs misuse the pattern field (e.g. material_type "stone" on a
  // "Façades placard" item). Restrict to the 5 known keys with a flat fallback.
  const safePattern = PATTERNS[pattern] ? pattern : 'flat';
  const fallbackBg = PATTERNS[safePattern](normalizedColor);
  const initials = label
    ? label.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
    : '?';

  // Pick legible initial color depending on the swatch lightness (WCAG-ish):
  // luminance > 0.55 → dark text, otherwise white text with a subtle shadow.
  let initialsColor = 'rgba(0,0,0,0.65)';
  let initialsShadow = '0 1px 2px rgba(255,255,255,0.55)';
  if (normalizedColor.startsWith('#') && normalizedColor.length >= 7) {
    const n = parseInt(normalizedColor.slice(1), 16);
    if (!Number.isNaN(n)) {
      const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
      const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      if (luma < 0.55) {
        initialsColor = 'rgba(255,255,255,0.92)';
        initialsShadow = '0 1px 2px rgba(0,0,0,0.55)';
      }
    }
  }

  return (
    <div
      className={`relative shrink-0 overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: rounded,
        border: '1px solid var(--c-border)',
        background: src ? `#000` : fallbackBg,
      }}
      aria-label={label}
      data-pattern={safePattern}
      data-color={normalizedColor}
    >
      {src ? (
        <img src={src} alt={label || ''} className="w-full h-full object-cover" />
      ) : (
        <>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.18) 0%, transparent 55%)',
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="text-[10px] font-bold tracking-[0.18em] hud-tabular"
              style={{ color: initialsColor, textShadow: initialsShadow }}
            >
              {initials}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default MaterialThumbnail;
